import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MessageCircle, BarChart3, Rocket, Mail, Globe, MapPin } from 'lucide-react';

const C = {
  bg: '#05091a', bg2: '#080f24', bg3: '#0c1530',
  card: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.08)',
  lime: '#bef264', cyan: '#67e8f9',
  white: '#ffffff', gray1: '#e2e8f0', gray2: '#94a3b8', gray3: '#475569',
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '12px 14px', background: C.bg3,
  border: `1px solid ${C.border}`, borderRadius: 10, color: C.white,
  fontSize: 14, outline: 'none', boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 12, fontWeight: 600,
  color: C.gray2, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em',
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div style={{ marginBottom: 20 }}>
    <label style={labelStyle}>{label}</label>
    {children}
  </div>
);

export default function Contact() {
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '',
    company: '', phone: '', interest: 'demo', message: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const set = (key: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm(prev => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/leads/contact-inquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `${form.firstName} ${form.lastName}`,
          email: form.email,
          company: form.company,
          phone: form.phone,
          interest: form.interest,
          message: form.message,
          source: 'contact-page',
        }),
      });
      if (!res.ok) throw new Error('Submission failed');
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again or email us directly at hello@eis-tech.co.uk');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: C.bg, fontFamily: 'Inter, system-ui, sans-serif', color: C.white }}>

      {/* Nav */}
      <nav style={{ borderBottom: `1px solid ${C.border}`, backgroundColor: 'rgba(5,9,26,0.9)', backdropFilter: 'blur(16px)', position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: 1220, margin: '0 auto', padding: '0 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 64 }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', color: C.white }}>
            <svg width="30" height="30" viewBox="0 0 512 512" fill="none">
              <defs>
                <linearGradient id="nbg" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#0EA5E9"/><stop offset="100%" stopColor="#0369A1"/></linearGradient>
                <linearGradient id="nlf" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#84CC16"/><stop offset="100%" stopColor="#3F6212"/></linearGradient>
              </defs>
              <path d="M256 56 C390 56 456 150 456 256" fill="none" stroke="url(#nbg)" strokeWidth="28" strokeLinecap="round"/>
              <path d="M56 256 C56 150 122 56 256 56" fill="none" stroke="url(#nbg)" strokeWidth="28" strokeLinecap="round"/>
              <circle cx="256" cy="180" r="38" fill="url(#nbg)"/>
              <path d="M170 260 Q256 340 256 320 Q256 340 342 260" fill="url(#nbg)"/>
              <path d="M256 340 Q140 340 100 420 Q180 380 256 456 Z" fill="url(#nlf)"/>
              <path d="M256 340 Q372 340 412 420 Q332 380 256 456 Z" fill="url(#nlf)"/>
            </svg>
            <span style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.2, color: '#22c55e' }}>EIS</span>
          </Link>
          <div style={{ display: 'flex', gap: 12 }}>
            <Link to="/login" style={{ color: C.gray2, textDecoration: 'none', fontSize: 14, padding: '8px 4px' }}>Sign in</Link>
            <Link to="/register" style={{ backgroundColor: C.lime, color: '#000', borderRadius: 8, padding: '9px 20px', fontSize: 14, fontWeight: 700, textDecoration: 'none' }}>Get started</Link>
          </div>
        </div>
      </nav>

      {submitted ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 64px)', padding: 24 }}>
          <div style={{ textAlign: 'left', maxWidth: 500 }}>
            <div style={{ width: 72, height: 72, borderRadius: '50%', background: `${C.lime}18`, border: `2px solid ${C.lime}`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 28 }}>
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={C.lime} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20,6 9,17 4,12"/></svg>
            </div>
            <h1 style={{ fontSize: 32, fontWeight: 800, marginBottom: 16 }}>Message received!</h1>
            <p style={{ color: C.gray2, fontSize: 16, lineHeight: 1.7, marginBottom: 32 }}>
              Thanks, {form.firstName}. We've received your enquiry and will be in touch at <strong style={{ color: C.white }}>{form.email}</strong> within one business day.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-start', flexWrap: 'wrap' }}>
              <Link to="/" style={{ background: C.card, border: `1px solid ${C.border}`, color: C.white, borderRadius: 10, padding: '12px 28px', fontSize: 14, fontWeight: 600, textDecoration: 'none' }}>Back to home</Link>
              <Link to="/register" style={{ backgroundColor: C.lime, color: '#000', borderRadius: 10, padding: '12px 28px', fontSize: 14, fontWeight: 700, textDecoration: 'none' }}>Start free trial</Link>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ maxWidth: 1220, margin: '0 auto', padding: '72px 28px' }}>
          <div className="contact-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: 80, alignItems: 'start' }}>

            {/* Left — info */}
            <div style={{ textAlign: 'left' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: `${C.lime}18`, border: `1px solid ${C.lime}30`, borderRadius: 20, padding: '4px 14px', fontSize: 11, fontWeight: 700, color: C.lime, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 24 }}>
                Talk to our team
              </div>
              <h1 style={{ fontSize: 'clamp(28px, 3vw, 44px)', fontWeight: 800, lineHeight: 1.15, letterSpacing: '-0.025em', marginBottom: 20 }}>
                Let's talk about<br/>
                <span style={{ background: `linear-gradient(135deg, ${C.lime}, ${C.cyan})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>your compliance</span>
              </h1>
              <p style={{ color: C.gray2, fontSize: 15, lineHeight: 1.75, marginBottom: 40 }}>
                Whether you need a personalised demo, pricing information, or just want to understand how EIS Tech fits your organisation — we're here to help.
              </p>

              {/* What to expect */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginBottom: 40 }}>
                {[
                  { icon: <Calendar size={22} />, title: 'Personalised demo', desc: 'A live walkthrough tailored to your industry and the frameworks you need.' },
                  { icon: <MessageCircle size={22} />, title: 'Expert consultation', desc: 'Speak with a compliance specialist who understands your regulatory environment.' },
                  { icon: <BarChart3 size={22} />, title: 'Custom pricing', desc: 'Transparent pricing based on your company size and framework requirements.' },
                  { icon: <Rocket size={22} />, title: 'Fast onboarding', desc: 'Get your first framework live in under 10 minutes after approval.' },
                ].map(item => (
                  <div key={item.title} style={{ display: 'flex', gap: 16 }}>
                    <span style={{ flexShrink: 0, marginTop: 2, color: C.lime }}>{item.icon}</span>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{item.title}</div>
                      <div style={{ color: C.gray2, fontSize: 13, lineHeight: 1.6 }}>{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Contact info */}
              <div style={{ background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.gray3, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 16 }}>Direct contact</div>
                {[
                  { icon: <Mail size={16} />, label: 'Email', value: 'hello@eis-tech.co.uk' },
                  { icon: <Globe size={16} />, label: 'Website', value: 'eis-tech.co.uk' },
                  { icon: <MapPin size={16} />, label: 'Location', value: 'United Kingdom' },
                ].map(item => (
                  <div key={item.label} style={{ display: 'flex', gap: 12, marginBottom: 14, fontSize: 14 }}>
                    <span style={{ color: C.lime, flexShrink: 0 }}>{item.icon}</span>
                    <div>
                      <div style={{ color: C.gray3, fontSize: 11, marginBottom: 2 }}>{item.label}</div>
                      <div style={{ color: C.gray1 }}>{item.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — form */}
            <div style={{ background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 20, padding: '40px 44px' }}>
              <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 6 }}>Send us a message</h2>
              <p style={{ color: C.gray2, fontSize: 14, marginBottom: 32 }}>We'll respond within one business day.</p>

              <form onSubmit={handleSubmit}>
                <div className="contact-field-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' }}>
                  <Field label="First name *">
                    <input style={inputStyle} placeholder="Sarah" value={form.firstName} onChange={set('firstName')} required/>
                  </Field>
                  <Field label="Last name *">
                    <input style={inputStyle} placeholder="Mitchell" value={form.lastName} onChange={set('lastName')} required/>
                  </Field>
                </div>

                <Field label="Work email *">
                  <input style={inputStyle} type="email" placeholder="sarah@company.com" value={form.email} onChange={set('email')} required/>
                </Field>

                <div className="contact-field-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' }}>
                  <Field label="Company *">
                    <input style={inputStyle} placeholder="Acme Corp" value={form.company} onChange={set('company')} required/>
                  </Field>
                  <Field label="Phone">
                    <input style={inputStyle} type="tel" placeholder="+44 7700 900000" value={form.phone} onChange={set('phone')}/>
                  </Field>
                </div>

                <Field label="I'm interested in">
                  <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.interest} onChange={set('interest')}>
                    <option value="demo">Product demo</option>
                    <option value="trial">Free trial</option>
                    <option value="pricing">Pricing information</option>
                    <option value="consultation">Compliance consultation</option>
                    <option value="partnership">Partnership / reseller</option>
                    <option value="other">Something else</option>
                  </select>
                </Field>

                <Field label="Message">
                  <textarea
                    style={{ ...inputStyle, resize: 'vertical', minHeight: 120 }}
                    placeholder="Tell us about your compliance requirements, current challenges, or any specific frameworks you're targeting…"
                    value={form.message}
                    onChange={set('message')}
                    rows={5}
                  />
                </Field>

                {error && (
                  <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 14, color: '#fca5a5' }}>
                    {error}
                  </div>
                )}

                <button type="submit" disabled={loading} style={{
                  width: '100%', backgroundColor: loading ? C.bg3 : C.lime, color: loading ? C.gray3 : '#000',
                  border: 'none', borderRadius: 10, padding: '14px 32px', fontSize: 15, fontWeight: 700,
                  cursor: loading ? 'not-allowed' : 'pointer', marginBottom: 16, transition: 'all 0.15s',
                }}>
                  {loading ? 'Sending…' : 'Send message'}
                </button>

                <p style={{ fontSize: 12, color: C.gray3, textAlign: 'center' }}>
                  Or go straight to{' '}
                  <Link to="/register" style={{ color: C.lime, textDecoration: 'none', fontWeight: 600 }}>request trial access →</Link>
                </p>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer style={{ borderTop: `1px solid ${C.border}`, marginTop: 80 }}>
        <div style={{ maxWidth: 1220, margin: '0 auto', padding: '24px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', color: C.gray3, fontSize: 13 }}>
            <svg width="20" height="20" viewBox="0 0 34 34" fill="none">
              <rect width="34" height="34" rx="9" fill={C.lime}/>
              <path d="M10 12h14M10 17h9M10 22h14" stroke="#000" strokeWidth="2.2" strokeLinecap="round"/>
            </svg>
            EIS Tech
          </Link>
          <span style={{ color: C.gray3, fontSize: 13 }}>© 2026 EIS Tech Ltd. All rights reserved.</span>
        </div>
      </footer>
      <style>{`
        @media (max-width: 768px) {
          .contact-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
          .contact-field-row { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
